import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, Router } from '@angular/router';
import { map, catchError, of } from 'rxjs';
import { ObterUsuarioUseCase } from '../../domain/auth/usecases/obter-usuario.usecase';
import { environment } from '../../../environments/environment';
import { AuthViewModel } from '../../features/auth/viewmodels/auth.viewmodel';
import { AUTHORIZATION_ROLE } from '../config/authorization-roles';

/**
 * Guard para controle de roles/permissões
 * Baseado no role_middleware.dart do Flutter
 */
export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state) => {
  const obterUsuarioUseCase = inject(ObterUsuarioUseCase);
  const authViewModel = inject(AuthViewModel);
  const router = inject(Router);

  const requiredRoles = route.data['roles'] as string[];
  const requiredPermissions = route.data['permissions'] as string[];

  // O bypass é explícito e só existe para facilitar o desenvolvimento local.
  // Ambientes HMG/produção devem usar skipAuth=false e validar o token real.
  if (environment.skipAuth && !environment.production) {
    return of(true);
  }

  // Se não há requisitos específicos, permite acesso
  if ((!requiredRoles || requiredRoles.length === 0) &&
      (!requiredPermissions || requiredPermissions.length === 0)) {
    return of(true);
  }

  return obterUsuarioUseCase.execute().pipe(
    map(user => {
      if (!user) {
        const destination = authViewModel.isAuthenticated()
          ? ['/acesso-negado']
          : ['/login'];
        return authViewModel.isAuthenticated()
          ? router.createUrlTree(destination)
          : router.createUrlTree(destination, {
              queryParams: { returnUrl: state.url }
            });
      }

      // Verifica roles se especificados
      if (requiredRoles && requiredRoles.length > 0) {
        const hasRequiredRole = requiredRoles.some(role => user.roles.includes(role));

        if (!hasRequiredRole) {
          return router.createUrlTree(['/acesso-negado'], {
            queryParams: {
              reason: 'insufficient_role',
              required: requiredRoles.join(','),
              current: user.roles.join(',')
            }
          });
        }
      }

      // Verifica permissões se especificadas
      if (requiredPermissions && requiredPermissions.length > 0) {
        const hasRequiredPermission = requiredPermissions.some(permission =>
          user.permissions.includes(permission)
        );

        if (!hasRequiredPermission) {
          return router.createUrlTree(['/acesso-negado'], {
            queryParams: {
              reason: 'insufficient_permission',
              required: requiredPermissions.join(','),
              current: user.permissions.join(',')
            }
          });
        }
      }

      return true;
    }),
    catchError(() => {
      return of(router.createUrlTree(['/acesso-negado']));
    })
  );
};
